import type { Action, ActionCreatorWithDeps } from '@bridebook/web/lib/types';
import { useDispatch } from '@bridebook/web/lib/utils';
import { useMemo } from 'react';

/**
 * Marks the planner's Redux writes on the actions themselves, so code can tell them apart:
 * - Each plain action the planner dispatches gets `meta.source`.
 * - A deps creator resolves to an action only later, inside the store, so it goes out in a
 *   marked wrapper. The deps resolver runs it with the planner's dispatch and stamps the result.
 * - Outcome epics copy the trigger's meta onto their follow-ups, failures included. A contract
 *   test over the lexicon's outcome entries holds this.
 * - Hooks with their own dispatching flows (enquiries, event-move analytics) get this dispatch
 *   injected.
 * An unstamped action is the couple's. Firestore snapshot echoes are never attributed: one
 * snapshot can carry writes from both sides.
 */

export const AGENT_ACTION_SOURCE = 'aiPlanner' as const;

// How analytics and URLs name the planner as the origin of an event or a visit.
export const PLANNER_ANALYTICS_SOURCE = 'aiWeddingPlanner' as const;

export type AgentActionMeta = {
  source: typeof AGENT_ACTION_SOURCE;
  // Links a dispatched intent to its epic follow-ups.
  dispatchId?: string;
};

type AppDispatch = ReturnType<typeof useDispatch>;

// A dispatched deps creator, as the deps resolver receives it.
type DepsCreator = ReturnType<ActionCreatorWithDeps<Action>>;

// A plain action as it travels the store — possibly already carrying meta.
export type StoreAction = Action & { meta?: Record<string, unknown> };

// A plain action confirmed as the planner's by the stamp on its meta.
export type AgentSourcedAction = Action & { meta: AgentActionMeta };

// What actually travels the middleware chain ahead of the deps resolver.
type ChainAction = Action | DepsCreator;
type ChainNext = (action: ChainAction) => unknown;

// A real action, not a deps creator or the Promise an async creator leaks through the resolver.
// Only these may be stamped: spreading anything else would break it.
export const isPlainAction = (action: unknown): action is StoreAction =>
  typeof action === 'object' && action !== null && typeof (action as Action).type === 'string';

const withAgentSource = <A extends StoreAction>(action: A): A & { meta: AgentActionMeta } => ({
  ...action,
  meta: { ...action.meta, source: AGENT_ACTION_SOURCE },
});

/**
 * Marked wrappers of deps creators the planner dispatched. A creator has no action to stamp
 * until the store resolves it, so a fresh wrapper in this set carries the mark. The original
 * closure stays unmarked, so the UI can still dispatch it as the couple's.
 */
const markedCreators = new WeakSet<object>();

const markAgentCreator = (creator: DepsCreator): DepsCreator => {
  const marked: DepsCreator = (deps) => creator(deps);
  markedCreators.add(marked);
  return marked;
};

// True for the marked wrapper the planner dispatched a deps creator as.
export const isMarkedAgentCreator = (action: unknown): action is DepsCreator =>
  typeof action === 'function' && markedCreators.has(action);

// True when the action was dispatched by the planner (or inherits from it).
export const isAgentAction = (action: unknown): action is AgentSourcedAction =>
  isPlainAction(action) && action.meta?.source === AGENT_ACTION_SOURCE;

// Analytics props with the planner named as their `source` when the action is the planner's.
export const withAgentSourceProps = <P extends object>(action: unknown, properties: P) =>
  isAgentAction(action) ? { ...properties, source: action.meta.source } : properties;

// Stamps a plain action; anything else (a leaked promise) passes untouched.
export const stampAgentSource = (action: unknown): unknown =>
  isPlainAction(action) ? withAgentSource(action) : action;

// The planner's dispatch: it marks every action it sends. A deps creator is marked through a
// fresh wrapper, never itself, since the UI can dispatch the same closure later.
export const createAgentDispatch = (dispatch: AppDispatch): AppDispatch => {
  // The store resolves creators at runtime; react-redux's Dispatch type
  // doesn't know the convention, so the widening lives here, once.
  const dispatchChain = dispatch as unknown as ChainNext;
  const agentDispatch = (action: ChainAction) => {
    if (typeof action === 'function') return dispatchChain(markAgentCreator(action));
    if (!isPlainAction(action)) return dispatch(action);
    return dispatch(withAgentSource(action));
  };
  return agentDispatch as AppDispatch;
};

// The marking dispatch as a hook, for the executors' composition points.
export const useAgentDispatch = (): AppDispatch => {
  const dispatch = useDispatch();
  return useMemo(() => createAgentDispatch(dispatch), [dispatch]);
};
